import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo, Fragment } from "react";
import { CookieProperty, CookiePropertyDataTransmission, CookiePropertyListLessRelevant } from ".";
import { useBannerOrBlocker, useLegalLinks, useAppropriateSafeguards } from "../../../hooks";
import { reactNl2Br } from "@devowl-wp/react-utils";
import { GcmConsentType } from "../gcm";
import { CookiePropertyTechnicalDefinitionsTable } from ".";
const CookiePropertyList = (param)=>{
    let { cookie: { purpose, isProviderCurrentWebsite, provider, providerContact = {}, providerPrivacyPolicyUrl, providerLegalNoticeUrl, legalBasis, dataProcessingInCountries, dataProcessingInCountriesSpecialTreatments, isEmbeddingOnlyExternalResources, technicalDefinitions, codeDynamics, googleConsentModeConsentTypes }, isEssentialGroup, isDisabled } = param;
    const { i18n, iso3166OneAlpha2, websiteOperator, isGcm, designVersion } = useBannerOrBlocker();
    const { dataProcessingInUnsafeCountries, appropriateSafeguards } = useAppropriateSafeguards({
        dataProcessingInCountries,
        specialTreatments: dataProcessingInCountriesSpecialTreatments
    });
    // Calculate provider-specific details
    const { legalNotice, privacyPolicy, contactForm } = useLegalLinks();
    const providerData = useMemo(()=>{
        if (isProviderCurrentWebsite && websiteOperator) {
            const { address, country, contactEmail, contactPhone } = websiteOperator;
            return {
                provider: [
                    address,
                    iso3166OneAlpha2[country] || country
                ].filter(Boolean).join(", "),
                contact: {
                    email: contactEmail,
                    phone: contactPhone,
                    link: contactForm
                },
                legalNoticeUrl: legalNotice === false ? "" : legalNotice.url,
                privacyPolicyUrl: privacyPolicy === false ? "" : privacyPolicy.url
            };
        }
        return {
            provider,
            contact: providerContact,
            privacyPolicyUrl: providerPrivacyPolicyUrl,
            legalNoticeUrl: providerLegalNoticeUrl
        };
    }, [
        isProviderCurrentWebsite,
        provider,
        providerContact,
        providerPrivacyPolicyUrl,
        providerLegalNoticeUrl,
        websiteOperator,
        legalNotice,
        privacyPolicy,
        contactForm
    ]);
    const hasProviderContact = useMemo(()=>Object.values(providerData.contact).filter(Boolean).length > 0, [
        providerData.contact
    ]);
    return /*#__PURE__*/ _jsxs(Fragment, {
        children: [
            !!purpose && /*#__PURE__*/ _jsx(CookieProperty, {
                label: i18n.purpose,
                value: reactNl2Br(purpose)
            }),
            /*#__PURE__*/ _jsx(CookieProperty, {
                label: i18n.legalBasis,
                value: legalBasis === "legal-requirement" ? i18n.legalRequirement : legalBasis === "legitimate-interest" || isEssentialGroup ? i18n.legitimateInterest : i18n.consent
            }),
            isGcm && googleConsentModeConsentTypes.length > 0 && /*#__PURE__*/ _jsx(CookieProperty, {
                label: i18n.gcm.dataProcessingInService,
                printValueAs: "empty",
                children: /*#__PURE__*/ _jsx("div", {
                    style: {
                        display: "inline-block"
                    },
                    children: /*#__PURE__*/ _jsx(CookieProperty, {
                        printValueAs: "empty",
                        children: googleConsentModeConsentTypes.map((type)=>/*#__PURE__*/ _jsx(GcmConsentType, {
                                type: type,
                                isDisabled: isDisabled
                            }, type))
                    })
                })
            }),
            /*#__PURE__*/ _jsx(CookieProperty, {
                label: i18n.provider,
                value: providerData.provider,
                children: hasProviderContact && /*#__PURE__*/ _jsxs(Fragment, {
                    children: [
                        /*#__PURE__*/ _jsx(CookieProperty, {
                            label: i18n.providerContactPhone,
                            value: providerData.contact.phone,
                            printValueAs: "phone"
                        }),
                        /*#__PURE__*/ _jsx(CookieProperty, {
                            label: i18n.providerContactEmail,
                            value: providerData.contact.email,
                            printValueAs: "mail"
                        }),
                        /*#__PURE__*/ _jsx(CookieProperty, {
                            label: i18n.providerContactLink,
                            value: providerData.contact.link
                        })
                    ]
                })
            }),
            /*#__PURE__*/ _jsx(CookieProperty, {
                label: i18n.providerPrivacyPolicyUrl,
                value: providerData.privacyPolicyUrl
            }),
            /*#__PURE__*/ _jsx(CookieProperty, {
                label: i18n.providerLegalNoticeUrl,
                value: providerData.legalNoticeUrl
            }),
            designVersion < 10 && dataProcessingInUnsafeCountries.length > 0 && /*#__PURE__*/ _jsx(CookieProperty, {
                label: i18n.dataProcessingInUnsafeCountries,
                value: dataProcessingInUnsafeCountries.join(", ")
            }),
            designVersion < 10 && appropriateSafeguards.length > 0 && /*#__PURE__*/ _jsx(CookieProperty, {
                label: i18n.appropriateSafeguard,
                value: appropriateSafeguards.join(", ")
            }),
            /*#__PURE__*/ _jsxs(CookiePropertyListLessRelevant, {
                expandable: designVersion > 9,
                labelModifications: {
                    [i18n.technicalCookieName]: i18n.technicalCookieDefinitions
                },
                children: [
                    designVersion > 9 && /*#__PURE__*/ _jsx(CookiePropertyDataTransmission, {
                        dataProcessingInCountries: dataProcessingInCountries,
                        dataProcessingInCountriesSpecialTreatments: dataProcessingInCountriesSpecialTreatments
                    }),
                    !isEmbeddingOnlyExternalResources && /*#__PURE__*/ _jsx(CookiePropertyTechnicalDefinitionsTable, {
                        codeDynamics: codeDynamics,
                        definitions: technicalDefinitions
                    })
                ]
            })
        ]
    });
};
export { CookiePropertyList };
